import React, { useEffect, useState } from "react";
import api from "../utils/api";


export default function AdminTools() {
    const [adminKey, setAdminKey] = useState(() => localStorage.getItem("adminKey") || "");
    const [status, setStatus] = useState(null);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState("");

    // NEW: names refresh + lookup state
    const [namesMsg, setNamesMsg] = useState("");
    const [lookupInput, setLookupInput] = useState("AAPL, MSFT, BRK-B, GOOGL");
    const [lookupResult, setLookupResult] = useState(null);
    const [namesBusy, setNamesBusy] = useState(false);

    useEffect(() => {
        let timer;
        if (busy) timer = setInterval(checkStatus, 2500);
        return () => clearInterval(timer);
    }, [busy]);

    const saveKey = (v) => {
        setAdminKey(v);
        localStorage.setItem("adminKey", v);
    };

    const headers = { "x-admin-key": adminKey };

    // ----- Existing: Rebuild All NYSE Picks Cache -----
    const startRebuild = async () => {
        setMsg("");
        if (!adminKey) { setMsg("Enter admin key first."); return; }
        try {
            setBusy(true);
            const { data } = await api.post(`/api/admin/rebuild-all-nyse-cache`, {}, { headers, timeout: 60000 });
            setStatus(data);
            setMsg("Rebuild started.");
        } catch (e) {
            setBusy(false);
            setMsg("Failed to start rebuild.");
            console.error(e);
        }
    };

    const checkStatus = async () => {
        try {
            const { data } = await api.get(`/api/admin/rebuild-status`, { headers, timeout: 15000 });
            setStatus(data);
            if (!data.running) setBusy(false);
        } catch (e) {
            setBusy(false);
            setMsg("Status check failed.");
            console.error(e);
        }
    };

    const percent = status && status.total ? Math.min(100, Math.round((status.processed / status.total) * 100)) : 0;

    // ----- NEW: Refresh nyse_tickers.txt on server -----
    const refreshNyseNames = async () => {
        setNamesMsg("");
        setLookupResult(null);
        if (!adminKey) { setNamesMsg("Enter admin key first."); return; }
        try {
            setNamesBusy(true);
            const { data } = await api.post( `/api/admin/refresh-nyse-names`, {}, { headers, timeout: 60000 });
            if (data?.ok) {
                setNamesMsg(
                    `Updated ${data.file}. Kept ${data.kept} names (from ${data.downloaded}). In-memory map size: ${data.map_size}.`
                );
            } else {
                setNamesMsg("Failed: " + (data?.error || "unknown error"));
            }
        } catch (e) {
            const msg = e?.response?.data?.error || e.message || "request failed";
            setNamesMsg("Failed: " + msg);
        } finally {
            setNamesBusy(false);
        }
    };

    // ----- NEW: Quick tester for the names endpoint -----
    const lookupNames = async () => {
        setLookupResult(null);
        setNamesMsg("");
        const raw = (lookupInput || "").trim();
        if (!raw) { setNamesMsg("Enter at least one ticker."); return; }
        const tickers = raw.split(",").map(s => s.trim()).filter(Boolean);
        try {
            setNamesBusy(true);
            const { data } = await api.post( `/api/symbols/names`, { tickers }, { timeout: 20000 });
            setLookupResult(data || {});
            if (!data || Object.keys(data).length === 0) {
                setNamesMsg("No names returned (check nyse_tickers.txt or fallback).");
            }
        } catch (e) {
            const msg = e?.response?.data?.error || e.message || "request failed";
            setNamesMsg("Lookup failed: " + msg);
        } finally {
            setNamesBusy(false);
        }
    };

    return (
        <div className="p-4 sm:p-5">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4 text-blue-400">🔐 Admin Tools</h2>

            <div className="space-y-6 max-w-2xl">

                {/* Admin Key */}
                <div className="space-y-2 bg-gray-800 border border-gray-700 rounded-2xl p-4">
                    <label className="block">
                        <span className="text-sm text-gray-300">Admin API Key</span>
                        <input
                            type="password"
                            className="mt-1 w-full bg-gray-800 border border-gray-700 rounded px-3 py-2"
                            value={adminKey}
                            onChange={(e) => saveKey(e.target.value)}
                            placeholder="Enter your admin key"
                        />
                    </label>
                </div>

                {/* Existing: Rebuild NYSE Picks Cache */}
                <div className="space-y-3 bg-gray-800 border border-gray-700 rounded-2xl p-4">
                    <h3 className="text-lg font-semibold">NYSE Picks Cache</h3>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={startRebuild}
                            disabled={busy}
                            className={`px-4 py-2 rounded font-semibold ${busy ? "bg-gray-600" : "bg-blue-600 hover:bg-blue-500"}`}
                        >
                            {busy ? "Rebuilding…" : "Rebuild All NYSE Picks Cache"}
                        </button>
                        <button
                            onClick={checkStatus}
                            className="px-3 py-2 rounded bg-gray-700 hover:bg-gray-600"
                        >
                            Check Status
                        </button>
                    </div>

                    {msg && <div className="text-sm text-gray-300">{msg}</div>}
                    {status && (
                        <div className="mt-2 text-sm text-gray-300 space-y-1">
                            <div>Running: {String(status.running)}</div>
                            <div>Processed: {status.processed} / {status.total}</div>
                            {status.error && (
                                <div className={
                                    /rate limited|sleeping/i.test(status.error) ? "text-yellow-400" : "text-red-400"
                                }>
                                    {/rate limited|sleeping/i.test(status.error) ? "Notice" : "Error"}: {status.error}
                                </div>
                            )}
                            <div className="w-full h-2 bg-gray-800 rounded">
                                <div className="h-2 bg-blue-500 rounded" style={{ width: `${percent}%` }} />
                            </div>
                        </div>
                    )}
                </div>

                {/* NEW: Company Names section */}
                <div className="space-y-3 bg-gray-800 border border-gray-700 rounded-2xl p-4">
                    <h3 className="text-lg font-semibold">Company Names (Ticker → Name)</h3>
                    <p className="text-sm text-gray-300">
                        Refresh <code>nyse_tickers.txt</code> on the server and hot-reload the in-memory map used by <code>/api/symbols/names</code>.
                    </p>
                    <div className="flex items-center gap-3 flex-wrap">
                        <button
                            onClick={refreshNyseNames}
                            disabled={namesBusy || !adminKey}
                            className={`px-4 py-2 rounded font-semibold ${namesBusy ? "bg-gray-600" : "bg-indigo-600 hover:bg-indigo-500"}`}
                            title={!adminKey ? "Enter admin key first" : "Download & refresh names"}
                        >
                            {namesBusy ? "Refreshing…" : "Refresh NYSE Names"}
                        </button>
                        {namesMsg && <span className="text-sm text-gray-300">{namesMsg}</span>}
                    </div>

                    <div className="pt-2 border-t border-gray-700">
                        <label className="block text-sm text-gray-300 mb-1">Quick Lookup (comma-separated tickers)</label>
                        <div className="flex gap-2">
                            <input
                                className="flex-1 bg-gray-800 border border-gray-700 rounded px-3 py-2"
                                value={lookupInput}
                                onChange={(e) => setLookupInput(e.target.value)}
                                placeholder="AAPL, MSFT, BRK-B, GOOGL"
                            />
                            <button
                                onClick={lookupNames}
                                disabled={namesBusy}
                                className={`px-4 py-2 rounded font-semibold ${namesBusy ? "bg-gray-600" : "bg-gray-700 hover:bg-gray-600"}`}
                            >
                                {namesBusy ? "Looking…" : "Lookup Names"}
                            </button>
                        </div>

                        {lookupResult && (
                            <pre className="mt-3 text-xs bg-gray-900 border border-gray-800 rounded p-3 overflow-auto">
                                {JSON.stringify(lookupResult, null, 2)}
                            </pre>
                        )}
                    </div>
                </div>

            </div>
        </div>
    );
}
